/**
 * PUBLIC CLASS DISPLAY ORDER — presentation only.
 *
 * The public Classes page shows the classes that are newest and still filling
 * first. Ranking is computed server-side by the `public-class-ranking` edge
 * function, which reuses the SAME canonical resolvers as the admin
 * minimum-enrollment workflow (paid seats toward the minimum, never merely
 * occupied seats) and the same alias-aware, de-duplicated interest logic as
 * the high-interest alert scan.
 *
 * Nothing here changes class data, visibility rules, sort_order values,
 * registration, checkout or interest records — it only re-orders an
 * already-filtered list and derives a badge from live state.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ClassItem } from "@/data/classes";
import { slugOf } from "@/lib/classRegistry";

/** Ordering groups, lowest first on the page. Mirrors the edge function. */
export const DISPLAY_GROUP = {
  enrolling: 1,
  open: 2,
  special: 3,
  comingSoon: 4,
} as const;

export interface RankEntry {
  slug: string;
  group: number;
  rank: number;
  /** True when the parent-facing "New & Enrolling" badge applies right now. */
  badge: boolean;
}

export interface ClassRanking {
  bySlug: Map<string, RankEntry>;
  loaded: boolean;
}

const EMPTY: ClassRanking = { bySlug: new Map(), loaded: false };

export function useClassRanking(): ClassRanking {
  const [state, setState] = useState<ClassRanking>(EMPTY);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("public-class-ranking", {
          body: {},
        });
        if (error) throw error;
        const entries: RankEntry[] = Array.isArray(data?.entries) ? data.entries : [];
        if (!mounted) return;
        setState({ bySlug: new Map(entries.map((e) => [e.slug, e])), loaded: true });
      } catch {
        // Ranking is cosmetic: on failure the page keeps its existing order.
        if (mounted) setState({ bySlug: new Map(), loaded: true });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return state;
}

/** Unranked classes (e.g. legacy static entries) sit with the open classes. */
const DEFAULT_GROUP = DISPLAY_GROUP.open;

/**
 * Stable re-order of an already-visibility-filtered list.
 * Primary: group. Secondary: server rank inside the group. Final tie-break:
 * the caller's existing order (so the current curated order is preserved).
 */
export function applyDisplayOrder(list: ClassItem[], ranking: ClassRanking): ClassItem[] {
  if (!ranking.loaded || ranking.bySlug.size === 0) return list;
  return list
    .map((c, i) => {
      const e = ranking.bySlug.get(slugOf(c));
      return { c, i, group: e?.group ?? DEFAULT_GROUP, rank: e?.rank ?? Number.MAX_SAFE_INTEGER };
    })
    .sort((a, b) => a.group - b.group || a.rank - b.rank || a.i - b.i)
    .map((x) => x.c);
}

/** Live-derived badge flag — never stored on the class record. */
export function isNewAndEnrolling(c: ClassItem, ranking: ClassRanking): boolean {
  return ranking.bySlug.get(slugOf(c))?.badge === true;
}
